import dynamic from 'next/dynamic';
import { type ComponentType, type HTMLProps } from 'react';

import { logger as browserLogger } from '@/logger/logger.browser';

import { SvgName } from './types';

// map of svg names to img file name
const SVG_IMG: Record<SvgName, string> = {
  cigna: 'cigna-logo',
  essence: 'essence-logo',
  'green-circle-check': 'green-circle-check',
  'olympics-team-usa': 'olympics-team-usa-logo',
  'us-open-tennis': 'us-open-tennis-logo',
};

/**
 * Cached per name so each lazy component is created at most once and stays
 * referentially stable across renders.
 *
 * `dynamic()` may be called during render, but must not be called
 * unconditionally on every render: it returns a new lazy component each time,
 * which suspends, which schedules another render, which creates another one.
 * Under React 19 that loop never settles — React re-renders forever, commits
 * nothing, and throws nothing. Caching the result per name avoids the loop.
 */
const svgComponents: Partial<
  Record<SvgName, ComponentType<HTMLProps<SVGElement>>>
> = {};

const getSvgImg = (name: SvgName) => {
  const svgImgFile = SVG_IMG[name];

  if (!svgImgFile) {
    browserLogger.warn('Missing SVG name', { name });
    return null;
  }

  let SvgImg = svgComponents[name];
  if (!SvgImg) {
    SvgImg = dynamic<HTMLProps<SVGElement>>(
      () => import(`./img/${svgImgFile}.svg`),
    );
    svgComponents[name] = SvgImg;
  }

  return <SvgImg />;
};

export default getSvgImg;
